import React, { createContext, useState, useContext } from "react";
import EventLog from "../apiService/eventLog/eventLogApiService";
import { APIResponseType } from 'app/apiService/types';

// ----------- Type Definitions -----------
export interface EventLogType {
  _id?: string;
  user_id?: string;
  additional_details?: {
    email?: string;
  };
  event_type?: string;
  request_url?: string;
  originating_ip?: string;
  user_agent?: string;
  source?: string;
  source_type?: string;
  status?: 'SUCCESS' | 'FAILURE';
  message?: string;
  created_date?: string;
  __v?: number;
}

interface EventLogContextType {
  loading: boolean;
  error: string | null;
  success: boolean;
  eventLog: EventLogType;
  setError: React.Dispatch<React.SetStateAction<string>>;
  setSuccess: React.Dispatch<React.SetStateAction<boolean>>;
  setEventLog: React.Dispatch<React.SetStateAction<EventLogType>>;
  addEventLog: (eventLog: EventLogType, callback?: () => void) => Promise<void>;
  deleteEventLog: (eventLogId: string, callback?: () => void) => Promise<void>;
  editEventLog: (eventLogId: string, data: Partial<EventLogType>, callback?: () => void) => Promise<void>;
  getEventLogs: (params?: any, callback?: () => void) => Promise<void>;
}

// ----------- Context Create -----------
export const EventLogContext = createContext<EventLogContextType>({
  loading: false,
  error: null,
  success: false,
  eventLog: {},
  setError: () => {},
  setSuccess: () => {},
  setEventLog: () => {},
  addEventLog: async () => {},
  deleteEventLog: async () => {},
  editEventLog: async () => {},
  getEventLogs: async () => {},
});

// ----------- Provider Component -----------
export const EventLogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<boolean>(false);
  const [eventLog, setEventLog] = useState<EventLogType>({});

  const handleSuccess = (callback?: () => void) => {
    setSuccess(true);
    setTimeout(() => {
      setSuccess(false);
      if (callback) callback();
    }, 2000);
  };

  const getEventLogs = async (params?: any, callback?: () => void) => {
    setLoading(true);
    try {
      const response: APIResponseType = await EventLog.getEventLogs(params);
      if (response.errorCode) {
        setError(response.message || "");
      } else {
        setError("");
        handleSuccess(callback);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to fetch event logs");
    } finally {
      setLoading(false);
    }
  };

  const addEventLog = async (eventLog: EventLogType, callback?: () => void) => {
    setLoading(true);
    try {
      const response: APIResponseType = await EventLog.addEventLog(eventLog);
      if (response.errorCode) {
        setError(response.message || "");
      } else {
        setError("");
        handleSuccess(callback);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to add event log");
    } finally {
      setLoading(false);
    }
  };

  const deleteEventLog = async (eventLogId: string, callback?: () => void) => {
    setLoading(true);
    try {
      await EventLog.deleteEventLog(eventLogId);
      handleSuccess(callback);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to delete event log");
    } finally {
      setLoading(false);
    }
  };

  const editEventLog = async (eventLogId: string, data: Partial<EventLogType>, callback?: () => void) => {
    setLoading(true);
    try {
      await EventLog.editEventLog(eventLogId, data);
      handleSuccess(callback);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to edit event log");
    } finally {
      setLoading(false);
    }
  };

  const contextValue: EventLogContextType = {
    loading,
    error,
    success,
    eventLog,
    setError,
    setSuccess,
    setEventLog,
    addEventLog,
    deleteEventLog,
    editEventLog,
    getEventLogs,
  };

  return (
    <EventLogContext.Provider value={contextValue}>
      {children}
    </EventLogContext.Provider>
  );
};

// ----------- Custom Hook -----------
export const useEventLog = (): EventLogContextType => {
  const context = useContext(EventLogContext);
  if (!context) {
    throw new Error("useEventLog must be used within a EventLogProvider");
  }
  return context;
};
